import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { dashboardService } from '../../../../services/dashboardService';
import { locationService } from '../../../../services/locationService';
import type { OmsMapLocation } from '../types';
import { buildPerformanceModel, type PerformanceModel } from '../utils/performanceModel';
import type { Scope } from './useScope';

const STALE_MS = 5 * 60 * 1000;

export interface PerformanceData {
  model: PerformanceModel;
  /** Current-window resources payload (engineer rows for Level 3 scoping). */
  current: ReturnType<typeof useQuery<Awaited<ReturnType<typeof dashboardService.getOmsResources>>>>['data'];
  /** Previous-window payload (engineer deltas). */
  previous: ReturnType<typeof useQuery<Awaited<ReturnType<typeof dashboardService.getOmsResources>>>>['data'];
  isLoading: boolean;
  isFetching: boolean;
}

/**
 * Fetches the two scoped windows (current + previous equivalent) plus the
 * project→location picker, and composes the Explorer's three-level model.
 * Both windows always resolve to explicit from/to days so the previous
 * window is exactly the same length as the current one.
 */
export function usePerformanceData(scope: Scope, mapLocations: OmsMapLocation[] | null): PerformanceData {
  const currentQuery = useQuery({
    queryKey: ['oms-perf', scope.window.from, scope.window.to],
    queryFn: () => dashboardService.getOmsResources('custom', scope.window),
    staleTime: STALE_MS,
    placeholderData: keepPreviousData,
  });

  const previousQuery = useQuery({
    queryKey: ['oms-perf', scope.prevWindow.from, scope.prevWindow.to],
    queryFn: () => dashboardService.getOmsResources('custom', scope.prevWindow),
    staleTime: STALE_MS,
    placeholderData: keepPreviousData,
  });

  const pickerQuery = useQuery({
    queryKey: ['oms-projects-picker'],
    queryFn: () => dashboardService.getOmsProjectsForPicker(),
    staleTime: 10 * 60 * 1000,
  });

  // Canonical location names — the map slice only covers geo-tagged
  // locations, so names come from the location master.
  const locationsQuery = useQuery({
    queryKey: ['oms-locations-dropdown'],
    queryFn: () => locationService.getLocationsForDropdown(),
    staleTime: 10 * 60 * 1000,
  });

  const model = useMemo(() => {
    const locationNames = new Map<string, string>(
      (locationsQuery.data ?? []).map((loc) => [
        String(loc._id ?? loc.id ?? ''),
        loc.name || loc.locationName || 'Unnamed location',
      ])
    );
    return buildPerformanceModel(
      currentQuery.data ?? null,
      previousQuery.data ?? null,
      pickerQuery.data ?? [],
      mapLocations ?? [],
      locationNames
    );
  }, [currentQuery.data, previousQuery.data, pickerQuery.data, mapLocations, locationsQuery.data]);

  return {
    model,
    current: currentQuery.data,
    previous: previousQuery.data,
    isLoading: currentQuery.isLoading || pickerQuery.isLoading,
    isFetching: currentQuery.isFetching || previousQuery.isFetching,
  };
}
